import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 [&_svg]:text-current outline-none focus-visible:border-buttonFocus-ring focus-visible:ring-buttonFocus-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "bg-buttonDefault-bg text-buttonDefault-text shadow-buttonDefault-shadow hover:bg-buttonDefault-hover dark:text-primary-foreground dark:hover:text-primary-foreground [&_svg]:text-current dark:[&_svg]:text-primary-foreground dark:hover:[&_svg]:text-primary-foreground",
        destructive:
          "bg-buttonDestructive-bg text-buttonDestructive-text shadow-buttonDestructive-shadow hover:bg-buttonDestructive-hover focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60 dark:text-destructive-foreground dark:hover:text-destructive-foreground [&_svg]:text-current dark:[&_svg]:text-destructive-foreground dark:hover:[&_svg]:text-destructive-foreground",
        outline:
          "border bg-buttonOutline-bg text-buttonOutline-text shadow-buttonOutline-shadow hover:bg-buttonOutline-hover-bg hover:text-buttonOutline-hover-text border-buttonOutline-border-color dark:bg-input/40 dark:border-input/80 dark:hover:bg-input/60 dark:hover:border-input dark:text-foreground dark:hover:text-foreground [&_svg]:text-current dark:[&_svg]:text-foreground dark:hover:[&_svg]:text-foreground",
        secondary:
          "bg-buttonSecondary-bg text-buttonSecondary-text shadow-buttonSecondary-shadow hover:bg-buttonSecondary-hover dark:text-secondary-foreground dark:hover:text-secondary-foreground [&_svg]:text-current dark:[&_svg]:text-secondary-foreground dark:hover:[&_svg]:text-secondary-foreground",
        ghost:
          "bg-buttonGhost-bg text-buttonGhost-text hover:bg-buttonGhost-hover-bg hover:text-buttonGhost-hover-text dark:hover:bg-accent/50 text-muted-foreground dark:text-foreground hover:text-foreground dark:hover:text-foreground [&_svg]:text-current dark:[&_svg]:text-foreground hover:[&_svg]:text-foreground dark:hover:[&_svg]:text-foreground",
        link: "text-buttonLink-text underline-offset-4 hover:underline hover:text-buttonLink-hover dark:text-primary dark:hover:text-primary [&_svg]:text-current dark:[&_svg]:text-primary dark:hover:[&_svg]:text-primary",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  type,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";

  // Default to type="button" when rendering a native <button>. HTML spec
  // defaults <button> to type="submit", which silently submits the nearest
  // ancestor <form> on click. That has bitten us inside modal-portaled
  // pickers (e.g. ResponsiveCommandSelect trigger) — the user clicks the
  // trigger, an ancestor form submits, and the underlying flow advances
  // before the dropdown can open. Callers can still override by passing
  // type="submit" explicitly (e.g. the actual submit button in a form).
  // For asChild (Radix Slot) we leave it undefined — the child element
  // declares its own type.
  const resolvedType = asChild ? type : (type ?? "button");

  return (
    <Comp
      data-slot="button"
      type={resolvedType}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
